import { get } from 'lodash';
import { USER_ROLES } from '@/common/constants';
import { SECTION_STYLE_DEFAULTS } from '@/views/SuperAdminPortal/WebsiteConfig/SectionStylePanel.vue';

/**
 * Page-level Section Style capabilities, keyed by page type.
 *
 * Page styles apply to the whole page (heading/intro alignment, text color,
 * background and — where the page has CTAs — button color). Unlike homepage
 * sections. Each page only exposes the controls that match the components it
 * actually renders (e.g. About has Features/Team cards but no buttons).
 */
export const PAGE_STYLE_CAPABILITIES = {
  // About: Banner + Features/Team cards. The page has no CTA button, and the cards
  // have no buttons either, so button controls are hidden (page + card button).
  about: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: true },
  // Contact: style only, no layout changes.
  contact: { showAlignment: false, showText: true, showBackground: true, showButton: true, showCard: false },
  // Gallery: only heading/description + background + text. No cards, no buttons.
  gallery: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: false },
  // FAQs: background + text, optional accent/button. No cards, no alignment.
  faqs: { showAlignment: false, showText: true, showBackground: true, showButton: true, showCard: false },
  // Resources: card-heavy page, full set.
  resources: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: true },
  // Custom: TinyMCE body is styled inline; only page chrome (bg/text/button).
  custom: { showAlignment: false, showText: true, showBackground: true, showButton: true, showCard: false },
};

/**
 * Mixin that gives a page editor a reactive `pageStyle` object, the brand
 * palette (`selectedOrganization`), and helpers to hydrate/serialize it.
 *
 * The host component is expected to provide:
 *  - `school` (super-admin selected school id)
 *  - `allSchools` (list for super-admin)
 *  - `getLoggedInUser` getter (mapped from auth)
 */
export default {
  data() {
    return {
      PAGE_STYLE_CAPABILITIES,
      pageStyle: { ...SECTION_STYLE_DEFAULTS },
    };
  },
  computed: {
    selectedOrganization() {
      if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
        return (this.allSchools || []).find(sch => sch.id === this.school);
      }
      return get(this.getLoggedInUser, 'linked_entity');
    },
  },
  methods: {
    /** Merge persisted style over defaults so every control is present/reactive. */
    setPageStyle(persisted) {
      this.pageStyle = { ...SECTION_STYLE_DEFAULTS, ...(persisted || {}) };
    },
    /** Reset back to a clean defaults object (host should re-save to persist). */
    resetPageStyle() {
      this.pageStyle = { ...SECTION_STYLE_DEFAULTS };
    },
  },
};
